import { useCallback, useEffect, useRef, useState } from "react";
import { createOnboardingLink } from "../api/stripeApi/onboardingLink";
import { getConnectStatus } from "../api/stripeApi/connectStatus";
import { updateUserEmail } from "../api/userApi/updateUserEmail";
import { readAuthToken } from "../utils/axiosMiddleware";
import { shouldStopPolling } from "../utils/stripeCharge";
import { redirectToStripe } from "../utils/stripeRedirect";
import { getFullUser } from "../views/Home/Service";

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 24; // ~96s, bounded

/**
 * Stripe Connect payout-onboarding controller.
 *
 * `startOnboarding` fetches a hosted onboarding link and redirects the full page
 * to Stripe (SSN/bank PII is collected on Stripe's domain — never ours). On
 * return, `poll`/the effect refetches `GET /stripe/connect/status/:userId` on a
 * bounded interval until `payouts_enabled` is true, then stops. The client gate
 * is UX-only — the server re-checks `payouts_enabled` at charge/transfer time.
 * When payouts flip to enabled, `fullUser` is re-fetched once so the gate flags
 * (receivingAccountVerify etc.) reflect the new state without a manual reload.
 *
 * `trackStatus: false` skips the mount read, the bounded poll loop and the
 * visibilitychange listener. The refresh screen only needs `startOnboarding`
 * and the email prompt — it redirects away immediately, so polling payout
 * status there is up to ~96s of requests nobody reads.
 */
export const useConnectOnboarding = (dispatch, { trackStatus = true } = {}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailRequired, setEmailRequired] = useState(false);
  const pollCountRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const refreshedRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const checkStatus = useCallback(async () => {
    const token = readAuthToken();
    if (!token?.user_id) {
      return null;
    }
    const data = await getConnectStatus(token.user_id, dispatch);
    if (data && mountedRef.current) {
      setStatus(data);
      // Payouts just became enabled — refresh the profile once so the stale
      // fullUser gate flags update without a manual reload (GIG-41 parity).
      if (data.payouts_enabled && !refreshedRef.current) {
        refreshedRef.current = true;
        getFullUser(dispatch);
      }
    }
    return data;
  }, [dispatch]);

  const poll = useCallback(async () => {
    clearTimer();
    const data = await checkStatus();
    if (
      !mountedRef.current ||
      shouldStopPolling(data, pollCountRef.current, MAX_POLLS)
    ) {
      return;
    }
    pollCountRef.current += 1;
    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [checkStatus, clearTimer]);

  /**
   * Reports what happened rather than leaving the caller to infer it from
   * control flow. `window.location.assign` does not halt execution, so a
   * caller that treats "the await returned" as failure reports a successful
   * redirect as an error while the browser is still navigating.
   *
   * Returns `{navigated, emailRequired}`. Existing callers that ignore the
   * return value are unaffected.
   */
  const startOnboarding = useCallback(async () => {
    setLoading(true);
    const data = await createOnboardingLink();
    setLoading(false);
    // A payee invited by SMS has no email yet, and Stripe will not open an
    // account without one. Ask for it instead of failing silently.
    if (data?.emailRequired) {
      setEmailRequired(true);
      return { navigated: false, emailRequired: true };
    }
    // SEC-4: go through the shared helper so the `?token=` JWT is stripped from
    // our URL BEFORE navigating to Stripe — a raw assign would leak it to
    // Stripe via the Referer header and leave it in browser history.
    return {
      navigated: redirectToStripe(data?.url),
      emailRequired: false,
    };
  }, []);

  /**
   * Returns the same `{navigated, emailRequired}` shape as `startOnboarding`,
   * so a caller can tell whether the retry actually got anywhere. Without it a
   * mint that fails *after* the email is saved leaves the page with no signal
   * at all — the modal closes onto a spinner that never resolves.
   *
   * A failed save keeps `emailRequired: true`: the modal stays open and the
   * user can correct the address, which is not a dead end.
   */
  const submitPayoutEmail = useCallback(
    async (email) => {
      setLoading(true);
      const saved = await updateUserEmail(email);
      setLoading(false);
      if (!saved) {
        return { navigated: false, emailRequired: true };
      }
      setEmailRequired(false);
      return startOnboarding();
    },
    [startOnboarding]
  );

  const dismissEmailPrompt = useCallback(() => setEmailRequired(false), []);

  // On mount, read the status once. Only enter the bounded poll LOOP when
  // onboarding is genuinely mid-flight (details submitted but payouts not yet
  // enabled — i.e. the return-from-Stripe case), so an idle first visit does a
  // single read instead of hammering the status endpoint 24 times. Always clean
  // up the timer + mark unmounted on teardown.
  useEffect(() => {
    mountedRef.current = true;
    if (!trackStatus) {
      return () => {
        mountedRef.current = false;
      };
    }
    const readStatus = async () => {
      const data = await checkStatus();
      if (
        mountedRef.current &&
        data?.details_submitted &&
        !data?.payouts_enabled
      ) {
        poll();
      }
    };
    readStatus();
    // Onboarding happens on Stripe's site, so the user comes BACK to this tab —
    // re-read on focus so the status reflects what they just completed without
    // waiting on the bounded poll or a manual refresh.
    //
    // A SINGLE read, deliberately — not `readStatus`. That would call `poll()`
    // again whenever onboarding is mid-flight, which is exactly the returning
    // user's state. `poll` clears only the one tracked timer and then awaits, so
    // a second entry orphans the first loop's timer: every refocus would leave
    // another uncancellable 4s loop running against the status endpoint.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkStatus();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mountedRef.current = false;
      clearTimer();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [checkStatus, poll, clearTimer, trackStatus]);

  return {
    status,
    loading,
    startOnboarding,
    refresh: checkStatus,
    emailRequired,
    submitPayoutEmail,
    dismissEmailPrompt,
  };
};
